import { SoloOutput } from '@/types/soloChoreographer';
import {
  injectSoloIntoLyrics,
  lyricsHaveSoloSection,
  lyricsHaveChoreographedSolo
} from '@features/SoloChoreographer/utils/soloOutputGenerator';
import type { RiffDnaConfig } from '@/context/RiffDnaContext';
import type { ParsedSunoOutput } from '@/types';

export interface PostProcessOptions {
  soloOutput: SoloOutput | null;
}

/**
 * Post-process generated lyrics to ensure proper formatting
 * and inject user-composed elements
 */
export function postProcessLyrics(
  rawLyrics: string, 
  options: PostProcessOptions
): string {
  let processedLyrics = rawLyrics;

  // 1. Inject Solo Choreography ONLY if not already present
  if (options.soloOutput && options.soloOutput.layerBlock) {
    // Skip if choreography already exists (Gemini included it based on prompt instructions)
    if (!lyricsHaveChoreographedSolo(processedLyrics)) {
      if (lyricsHaveSoloSection(processedLyrics)) {
        processedLyrics = injectSoloIntoLyrics(processedLyrics, options.soloOutput);
      } else {
        // No solo section found - try to insert before final chorus
        // Look for the last [Chorus] or [Final Chorus] or [Outro]
        const insertionPatterns = [
          /(\[Outro\])/i,
          /(\[Final Chorus\])/i,
          /(\n\[Chorus\](?![\s\S]*\[Chorus\]))/i  // Last chorus
        ];

        for (const pattern of insertionPatterns) {
          if (pattern.test(processedLyrics)) {
            const soloSection = `\n${options.soloOutput.sectionHeader}\n${options.soloOutput.layerBlock}\n\n`;
            processedLyrics = processedLyrics.replace(pattern, soloSection + '$1');
            break;
          }
        }
      }
    }
  }

  // 2. Clean up any double line breaks (more than 2)
  processedLyrics = processedLyrics.replace(/\n{4,}/g, '\n\n\n');

  // 3. Ensure proper spacing around section headers
  processedLyrics = processedLyrics.replace(/\]\n([^\n\[])/g, ']\n\n$1');

  return processedLyrics;
}

/**
 * Post-process a ParsedSunoOutput to inject Riff DNA content into Suno-facing fields.
 * - sunoStyle → appended to result.style (tags)
 * - sunoTags → injected into result.lyricsWithTags as bracketed meta-tags at contextual positions
 * - sunoExclude → appended to result.excludedStyle (negative tags)
 */
export function postProcessRiffDna(
  result: ParsedSunoOutput,
  riffDnaConfig: RiffDnaConfig
): ParsedSunoOutput {
  if (!riffDnaConfig.enabled) return result;

  const processed = { ...result };

  // Append riff character descriptors to style tags
  if (riffDnaConfig.sunoStyle) {
    processed.style = processed.style
      ? `${processed.style}, ${riffDnaConfig.sunoStyle}`
      : riffDnaConfig.sunoStyle;
  }

  // Append riff exclusions to negative tags
  if (riffDnaConfig.sunoExclude) {
    processed.excludedStyle = processed.excludedStyle
      ? `${processed.excludedStyle}, ${riffDnaConfig.sunoExclude}`
      : riffDnaConfig.sunoExclude;
  }

  // Build riff character block for lyrics (tells Suno HOW the riff should sound)
  const riffCharacterBlock = buildRiffCharacterBlock(riffDnaConfig);
  if (riffCharacterBlock) {
    processed.lyricsWithTags = riffCharacterBlock + '\n\n' + processed.lyricsWithTags;
  }

  // Inject arrangement meta-tags into lyrics at contextually appropriate positions
  if (riffDnaConfig.sunoTags.length > 0) {
    processed.lyricsWithTags = injectRiffTags(processed.lyricsWithTags, riffDnaConfig.sunoTags);
  }

  return processed;
}

/**
 * Post-process lyrics to ensure physics layers have actual content.
 * The LLM sometimes outputs empty layer headers like {LAYER-1 VOCAL PHYSICS}
 * with no tag content underneath. This replaces empty headers with the real
 * physics content from VocalForge and InstrumentForge.
 */
export function postProcessPhysicsLayers(
  lyrics: string,
  vocalArchitecture: string | undefined,
  instrumentForgePhysics: string | undefined,
): string {
  let result = lyrics;

  // Extract just the physics block from vocal architecture (LAYER-1 content)
  if (vocalArchitecture) {
    const vocalPhysics = extractLayer1Content(vocalArchitecture);
    if (vocalPhysics) {
      result = replaceEmptyLayer(result, 'LAYER-1 VOCAL PHYSICS', vocalPhysics);
    }
  }

  // Extract LAYER-2 through LAYER-5 from InstrumentForge physics
  if (instrumentForgePhysics) {
    const layerPatterns = [
      { key: 'LAYER-2 RHYTHM SECTION PHYSICS', regex: /\{LAYER-2 RHYTHM SECTION PHYSICS\}\n([\s\S]*?)(?=\n\{LAYER-|$)/i },
      { key: 'LAYER-3 GUITAR PHYSICS', regex: /\{LAYER-3 GUITAR PHYSICS\}\n([\s\S]*?)(?=\n\{LAYER-|$)/i },
      { key: 'LAYER-4 ATMOSPHERE PHYSICS', regex: /\{LAYER-4 ATMOSPHERE PHYSICS[^\}]*\}\n([\s\S]*?)(?=\n\{LAYER-|$)/i },
      { key: 'LAYER-5 SPECIALTY PHYSICS', regex: /\{LAYER-5 SPECIALTY PHYSICS[^\}]*\}\n([\s\S]*?)(?=\n\{LAYER-|$)/i },
    ];

    for (const { key, regex } of layerPatterns) {
      const match = instrumentForgePhysics.match(regex);
      if (match && match[1].trim()) {
        result = replaceEmptyLayer(result, key, match[1].trim());
      }
    }
  }

  return result;
}

/** Extract LAYER-1 tag lines from vocal architecture (everything between header and next section/end) */
function extractLayer1Content(vocalArch: string): string | null {
  const match = vocalArch.match(/\{LAYER-1 VOCAL PHYSICS\}\n([\s\S]*?)(?=\n\n|$)/);
  if (!match || !match[1].trim()) return null;
  // Only return if it has actual [Tag] content
  const content = match[1].trim();
  if (content.includes('[')) return content;
  return null;
}

/**
 * Replace an empty layer header in lyrics with actual content.
 * "Empty" means the header exists but has no [Tag] lines immediately following it.
 */
function replaceEmptyLayer(lyrics: string, layerKey: string, content: string): string {
  const escapedKey = layerKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Check if the layer header exists
  const headerRegex = new RegExp(`\\{${escapedKey}\\}`, 'i');
  if (!headerRegex.test(lyrics)) {
    // Header doesn't exist at all — inject it before the first [Verse] or at top
    const fullBlock = `{${layerKey}}\n${content}`;
    const verseMatch = lyrics.match(/^(\[Verse[^\]]*\])/im);
    if (verseMatch && verseMatch.index !== undefined) {
      return lyrics.slice(0, verseMatch.index) + fullBlock + '\n\n' + lyrics.slice(verseMatch.index);
    }
    return fullBlock + '\n\n' + lyrics;
  }

  // Header exists — check if it has content (at least one [Tag] line after it)
  const withContentRegex = new RegExp(
    `\\{${escapedKey}\\}\\n(\\[.+\\])`,
    'i'
  );
  if (withContentRegex.test(lyrics)) {
    // Already has content, don't replace
    return lyrics;
  }

  // Empty header — replace with header + actual content
  return lyrics.replace(headerRegex, `{${layerKey}}\n${content}`);
}

/**
 * Riff DNA is a single locked config reused verbatim across every track it's
 * applied to (e.g. every song on an album), but the LLM that generates it
 * isn't forbidden from baking a specific key/chord into its free-text fields
 * (sunoStyle, deliberateFlaw, mutation) -- so a config authored around "C
 * major" gets that phrase injected into every track's lyrics, including
 * tracks explicitly written in a different key. Best-effort text sanitizer,
 * not a guarantee: strips the "{letter}[#/b] {major/minor}" and hyphenated
 * chord-letter patterns ("C-G", "C5-G5") so the injected text doesn't
 * contradict a track's own specified key. Also fixes already-locked Riff DNA
 * snapshots saved before RIFF_DNA_SYSTEM_PROMPT forbade this.
 */
function sanitizeKeyReferences(text: string): string {
  if (!text) return text;
  return text
    .replace(/\b[A-G][#b]?\s*(major|minor|maj\.?|min\.?)\b/gi, "the track's key")
    .replace(/\b[A-G][#b]?\d?(?:-[A-G][#b]?\d?){1,}\b/g, 'chord change')
    .replace(/\b[A-G][#b]?5\b\s*/g, '')
    .replace(/\s{2,}/g, ' ');
}

/**
 * Build a compact riff character instruction block for Suno lyrics.
 *
 * These are vibe descriptors for the LLM to compose FROM, not text meant to
 * survive into the delivered song — so no line here may contain a raw field
 * label ("Riff Feel:", "Flaw:", "Riff Mutation:"). An earlier version
 * prefixed lines with those labels, and Suno rendered them as literal sung
 * text ("(Riff Feel: swing | Flaw: ...)") across at least two songs. Every
 * line is now plain parenthetical prose — describing the archetype/feel/
 * flaw/mutation, never naming the field it came from.
 */
function buildRiffCharacterBlock(config: RiffDnaConfig): string {
  const lines: string[] = [];
  if (config.sunoStyle) {
    lines.push(`(${config.archetype || 'Riff'}: ${sanitizeKeyReferences(config.sunoStyle)})`);
  }
  if (config.feel) {
    let feelLine = `(${config.feel}`;
    if (config.deliberateFlaw) feelLine += `, ${sanitizeKeyReferences(config.deliberateFlaw)}`;
    feelLine += ')';
    lines.push(feelLine);
  }
  if (config.mutation) {
    lines.push(`(${sanitizeKeyReferences(config.mutation)})`);
  }
  return lines.length > 0 ? lines.join('\n') : '';
}

/** Ensure a tag string has [brackets] — LLMs sometimes omit them */
function ensureBrackets(tag: string): string {
  const t = tag.trim();
  if (t.startsWith('[') && t.endsWith(']')) return t;
  const inner = t.replace(/^\[?/, '').replace(/\]?$/, '');
  return `[${inner}]`;
}

/**
 * Inject riff arrangement meta-tags at contextually appropriate positions in lyrics.
 * Matches tag content to section keywords (intro→top, verse→before verse, chorus→before chorus, etc.)
 */
function injectRiffTags(lyrics: string, tags: string[]): string {
  let result = lyrics;
  const unplaced: string[] = [];

  for (const raw of tags) {
    const tag = ensureBrackets(raw);
    // Skip if already present
    if (result.toLowerCase().includes(tag.toLowerCase())) continue;

    const tl = tag.toLowerCase();
    let placed = false;

    if (tl.includes('intro')) {
      // Intro tag goes at the very beginning
      result = `${tag}\n${result}`;
      placed = true;
    } else if (tl.includes('outro') || tl.includes('fade')) {
      placed = insertBefore(result, /^(\[Outro[^\]]*\])/im, tag, r => { result = r; });
    } else if (tl.includes('verse')) {
      placed = insertBefore(result, /^(\[Verse[^\]]*\])/im, tag, r => { result = r; });
    } else if (tl.includes('chorus')) {
      placed = insertBefore(result, /^(\[Chorus[^\]]*\])/im, tag, r => { result = r; });
    } else if (tl.includes('bridge') || tl.includes('break')) {
      placed = insertBefore(result, /^(\[Bridge[^\]]*\])/im, tag, r => { result = r; });
    } else if (tl.includes('solo')) {
      placed = insertBefore(result, /^(\[(?:Guitar )?Solo[^\]]*\])/im, tag, r => { result = r; });
    }

    if (!placed) unplaced.push(tag);
  }

  // Unmatched tags go at the very beginning as general arrangement cues
  if (unplaced.length > 0) {
    result = unplaced.join('\n') + '\n' + result;
  }

  return result;
}

/** Insert a tag line before the first match of a pattern (multiline). Returns true if placed. */
function insertBefore(
  lyrics: string,
  pattern: RegExp,
  tag: string,
  setter: (result: string) => void,
): boolean {
  const match = lyrics.match(pattern);
  if (match && match.index !== undefined) {
    setter(lyrics.slice(0, match.index) + tag + '\n' + lyrics.slice(match.index));
    return true;
  }
  return false;
}
